import { ChangeDetectorRef, Component, Input, inject } from '@angular/core';
import { FarmingInfo } from './models/farming-info';
import { MatTableDataSource } from '@angular/material/table';
import { PraticaProgressService } from '../../_core/services/pratica-progress.service';
import { SharedModule } from '../../_shared/shared.module';
import { IconService } from '../../_core/services/icon.service';
import { FarmingService } from '../../_core/services/farming.service';
import { BehaviorSubject, of, switchMap, tap } from 'rxjs';
import { DialogService } from 'src/app/_core/services/dialog.service';
import { FarmingInfoDialogComponent } from '../farming-info-dialog/farming-info-dialog.component';
import { FullScreenDialogConfig } from 'src/app/_config/full-screen-dialog-config';
import { MessageDialogComponent } from 'src/app/_shared/message-dialog/message-dialog.component';
import { ConfirmDialogConfig } from 'src/app/_config/confirm-dialog-config';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-farming-info-table',
  templateUrl: './farming-info-table.component.html',
  styleUrls: ['./farming-info-table.component.scss'],
  standalone: true,
  imports: [SharedModule],
})
export class FarmingInfoTableComponent {
  @Input() set isFarmingIncluded(isFarmingIncluded: boolean) {
    this._isFarmingIncluded = isFarmingIncluded;
    if (!isFarmingIncluded) {
      this.updateCompletionPercentage(isFarmingIncluded, []);
    } else {
      this.getTableData(isFarmingIncluded).subscribe();
    }
  }
  private _isFarmingIncluded = false;
  get isFarmingIncluded() {
    return this._isFarmingIncluded;
  }

  @Input()
  set idScheda(presentationSheetId: string) {
    if (presentationSheetId) {
      this._idScheda = presentationSheetId;
      //this.getTableData(this.isFarmingIncluded).subscribe();
    }
  }

  private _idScheda: string = '';

  get idScheda() {
    return this._idScheda;
  }

  dataSource = new MatTableDataSource<FarmingInfo>([]);

  displayedColumns: (keyof FarmingInfo | 'actions')[] = [
    'tipoAllevamento',
    'numeroCapi',
    'conduzioneStalle',
    'provincia',
    'canoneAffitto',
    'actions',
  ];

  completionPercentage = new BehaviorSubject<number>(0);
  private readonly percentageWeight = 1 / 3;

  readonly praticaProgressService = inject(PraticaProgressService);
  readonly farmingService = inject(FarmingService);
  readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private dialog: MatDialog,
    iconService: IconService,
    private dialogService: DialogService
  ) {
    iconService.getIcons(['edit', 'delete']);
  }

  ngOnDestroy(): void {
    this.completionPercentage.complete();
  }

  private onDelete(id: string) {
    return this.farmingService.delete(id).pipe(
      tap((res) => {
        this.dialogService.onSuccess();
      }),
      switchMap((_) => this.getTableData(this.isFarmingIncluded))
    );
  }

  private openConfirmDialog() {
    return this.dialog
      .open(MessageDialogComponent, new ConfirmDialogConfig())
      .afterClosed();
  }

  private manageFarmingsFormData(formData: FarmingInfo, id: string) {
    return (
      id
        ? this.farmingService.edit(formData, id, this.idScheda)
        : this.farmingService.add(formData, this.idScheda)
    ).pipe(switchMap((_) => this.getTableData(this.isFarmingIncluded)));
  }

  private updateCompletionPercentage(
    isFarmingIncluded: boolean,
    farmings: FarmingInfo[]
  ) {
    const tableCompletionPercentage = isFarmingIncluded
      ? farmings.length > 0
        ? 100
        : 0
      : 100;
    const relativeCompletionPercentage =
      this.praticaProgressService.calcComplexCompletionPercentage(
        tableCompletionPercentage,
        this.percentageWeight
      );
    this.completionPercentage.next(relativeCompletionPercentage);
  }

  getTableData(isFarmingIncluded: boolean) {
    return this.farmingService.getById(this.idScheda).pipe(
      tap((res) => {
        this.dataSource.data = res;
        this.updateCompletionPercentage(isFarmingIncluded, res);
        this.cdr.detectChanges();
      })
    );
  }

  showFarmingDialog(farmingsInfo?: FarmingInfo) {
    return this.dialogService
      .open(FarmingInfoDialogComponent, {
        ...FullScreenDialogConfig,
        data: {
          farming: farmingsInfo,
        },
      })
      .afterClosed();
  }

  openFarmingInfoDialog(farmingInfo?: FarmingInfo) {
    this.showFarmingDialog(farmingInfo)
      .pipe(
        switchMap((formData: FarmingInfo) =>
          formData
            ? this.manageFarmingsFormData(formData, farmingInfo?.id || '')
            : of(null)
        )
      )
      .subscribe((farmings) => farmings && this.dialogService.onSuccess());
  }

  deleteFarmingInfo(event: FarmingInfo) {
    this.openConfirmDialog()
      .pipe(
        switchMap((confirm) => (confirm ? this.onDelete(event.id) : of(null)))
      )
      .subscribe();
  }
}
